import { type FC, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdPerson, MdEmail, MdLogout, MdDarkMode, MdLightMode, MdCalendarToday, MdEdit, MdCheck, MdClose, MdPalette, MdCameraAlt } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useProfile } from '../hooks/useProfile';
import ThemeToggle from '../components/ThemeToggle';
import { ProfileSkeleton } from '../components/Skeleton';

const Profile: FC = () => {
  const { user, signOut } = useAuth();
  const { theme, colorTheme, setColorTheme } = useTheme();
  const { profile, uploading, uploadPhoto, updateProfile } = useProfile(user?.id);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [username, setUsername] = useState(() => {
    return localStorage.getItem('taskapp_username') || user?.email?.split('@')[0] || 'User';
  });
  const [tempUsername, setTempUsername] = useState(username);

  const colorOptions = [
    { name: 'Blue', value: 'blue', color: '#00FFFF' },
    { name: 'Green', value: 'green', color: '#00FF00' },
    { name: 'Purple', value: 'purple', color: '#8B00FF' },
    { name: 'Pink', value: 'pink', color: '#FF00FF' },
    { name: 'Orange', value: 'orange', color: '#FF6B00' },
    { name: 'Yellow', value: 'yellow', color: '#FFFF00' },
  ];

  const handleSaveUsername = () => {
    if (tempUsername.trim()) {
      setUsername(tempUsername.trim());
      localStorage.setItem('taskapp_username', tempUsername.trim());
      
      // Also update in Supabase
      if (user?.id) {
        updateProfile({ username: tempUsername.trim() }).catch((error) => {
          console.error('Failed to update username in database:', error);
        });
      }
      
      setIsEditingUsername(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      await uploadPhoto(file);
      toast.success('Profile photo updated successfully!');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    }
  };

  const handleCancelEdit = () => {
    setTempUsername(username);
    setIsEditingUsername(false);
  };

  const handleColorChange = (color: string) => {
    // Update the global color theme through our hook
    setColorTheme(color as 'blue' | 'green' | 'purple' | 'pink' | 'orange' | 'yellow');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Get accent color with opacity for gradient
  const getAccentColor = () => {
    const colors: Record<string, string> = {
      blue: '0, 255, 255',
      green: '0, 255, 0',
      purple: '139, 0, 255',
      pink: '255, 0, 255',
      orange: '255, 107, 0',
      yellow: '255, 255, 0'
    };
    return colors[colorTheme];
  };

  if (!user) {
    return (
      <div 
        className="min-h-screen py-8 px-4"
        style={{
          background: theme === 'dark'
            ? `linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(${getAccentColor()}, 0.03) 100%)`
            : `linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(${getAccentColor()}, 0.05) 100%)`
        }}
      >
        <div className="max-w-2xl mx-auto">
          <ProfileSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-6 sm:py-8 px-3 sm:px-4"
      style={{
        background: theme === 'dark'
          ? `linear-gradient(135deg, rgba(17, 24, 39, 1) 0%, rgba(${getAccentColor()}, 0.03) 100%)`
          : `linear-gradient(135deg, rgba(240, 244, 248, 1) 0%, rgba(${getAccentColor()}, 0.05) 100%)`
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* Profile Card */}
        <div className="
          dark:bg-dark-surface
          bg-light-surface
          rounded-lg
          shadow-lg
          border-2
          dark:border-theme
          border-theme
          p-6 sm:p-8
        ">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
              Profile
            </h1>
            <ThemeToggle />
          </div>

          {/* Avatar Section */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b dark:border-dark-hover border-gray-200">
            <div className="relative group">
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt="Profile"
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover shadow-neon border-4 border-theme"
                />
              ) : (
                <div className="
                  w-20 h-20 sm:w-24 sm:h-24
                  rounded-full
                  flex items-center justify-center
                  bg-gradient-to-br from-theme to-neon-purple
                  shadow-neon
                  border-4
                  border-theme
                ">
                  <MdPerson className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                </div>
              )}
              
              {/* Upload overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="
                  absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 
                  rounded-full 
                  bg-black bg-opacity-50 
                  flex items-center justify-center 
                  opacity-0 group-hover:opacity-100 
                  transition-opacity 
                  cursor-pointer
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                <div className="text-center text-white">
                  {uploading ? (
                    <div className="animate-spin w-6 h-6 border-2 border-white border-t-transparent rounded-full mx-auto" />
                  ) : (
                    <>
                      <MdCameraAlt className="w-6 h-6 mx-auto" />
                      <span className="text-xs mt-1 block">Upload</span>
                    </>
                  )}
                </div>
              </button>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            <div className="flex-1 text-center sm:text-left w-full">
              {isEditingUsername ? (
                <div className="flex flex-col sm:flex-row items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={tempUsername}
                    onChange={(e) => setTempUsername(e.target.value)}
                    className="
                      w-full sm:w-auto
                      px-3 py-2
                      text-lg sm:text-xl font-bold
                      border-2 rounded-lg
                      dark:border-theme border-theme
                      dark:bg-gray-900 bg-white
                      dark:text-white text-gray-900
                      focus:outline-none focus:ring-2 dark:focus:ring-theme focus:ring-theme
                    "
                    autoFocus
                  />
                  <button
                    onClick={handleSaveUsername}
                    className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                    title="Save"
                  >
                    <MdCheck className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                    title="Cancel"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-2xl font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                    {username}
                  </h2>
                  <button
                    onClick={() => setIsEditingUsername(true)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title="Edit username"
                  >
                    <MdEdit className="w-4 h-4" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }} />
                  </button>
                </div>
              )}
              <p style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                Task Management User
              </p>
            </div>
          </div>

          {/* User Details */}
          <div className="space-y-6 mb-8">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="
                p-3
                rounded-lg
                dark:bg-dark-card
                bg-white
                border-2
                dark:border-theme
                border-theme
              ">
                <MdEmail className="w-6 h-6 dark:text-theme text-theme" />
              </div>
              <div className="flex-1">
                <p className="text-sm mb-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                  Email Address
                </p>
                <p className="font-medium" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                  {user.email}
                </p>
              </div>
            </div>

            {/* Account Created */}
            <div className="flex items-start gap-4">
              <div className="
                p-3
                rounded-lg
                dark:bg-dark-card
                bg-white
                border-2
                dark:border-neon-purple
                border-purple-400
              ">
                <MdCalendarToday className="w-6 h-6 dark:text-neon-purple text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm mb-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                  Account Created
                </p>
                <p className="font-medium" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                  {new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Theme Preference */}
            <div className="flex items-start gap-4">
              <div className="
                p-3
                rounded-lg
                dark:bg-dark-card
                bg-white
                border-2
                dark:border-neon-green
                border-green-400
              ">
                {theme === 'dark' ? (
                  <MdDarkMode className="w-6 h-6 dark:text-neon-green text-green-600" />
                ) : (
                  <MdLightMode className="w-6 h-6 dark:text-neon-green text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm mb-1" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                  Theme Preference
                </p>
                <p className="font-medium capitalize" style={{ color: theme === 'dark' ? '#ffffff' : '#111827' }}>
                  {theme} Mode
                </p>
              </div>
            </div>

            {/* Accent Color Selector */}
            <div className="flex items-start gap-4">
              <div className="
                p-3
                rounded-lg
                dark:bg-dark-card
                bg-white
                border-2
                dark:border-neon-purple
                border-purple-400
              ">
                <MdPalette className="w-6 h-6 dark:text-neon-purple text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm mb-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#4b5563' }}>
                  Accent Color Theme
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => handleColorChange(color.value)}
                      style={{ backgroundColor: color.color }}
                      className={`
                        relative
                        w-full aspect-square
                        rounded-lg
                        transition-all duration-200
                        hover:scale-110
                        ${colorTheme === color.value ? 'ring-4 ring-offset-2 dark:ring-offset-dark-surface ring-offset-white scale-110' : ''}
                        shadow-lg
                        hover:shadow-xl
                      `}
                      title={color.name}
                    >
                      {colorTheme === color.value && (
                        <MdCheck className="w-6 h-6 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-lg" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-2 capitalize" style={{ color: theme === 'dark' ? '#6b7280' : '#6b7280' }}>
                  Current: {colorTheme}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {/* Advanced Theme Settings Button - Commented out for now */}
            {/* 
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/theme-settings')}
              className="
                w-full
                flex items-center justify-center gap-2
                px-6 py-3
                rounded-lg
                bg-gradient-to-r from-theme to-neon-purple
                text-white
                font-semibold
                focus:outline-none
                focus:ring-2
                focus:ring-theme
                focus:ring-offset-2
                dark:focus:ring-offset-dark-bg
                shadow-lg
                hover:shadow-xl
                transition-all
              "
            >
              <MdPalette className="w-5 h-5" />
              Advanced Theme Settings
            </motion.button>
            */}

            {/* Sign Out Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSignOut}
              className="
                w-full
                flex items-center justify-center gap-2
                px-6 py-3
                rounded-lg
                bg-gradient-to-r from-red-500 to-red-600
                text-white
                font-semibold
                hover:from-red-600 hover:to-red-700
                focus:outline-none
                focus:ring-2
                focus:ring-red-500
                focus:ring-offset-2
                dark:focus:ring-offset-dark-bg
                shadow-lg
                hover:shadow-xl
                transition-all
              "
            >
              <MdLogout className="w-5 h-5" />
              Sign Out
            </motion.button>

            {/* Back to Dashboard */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 rounded-lg border-2 font-semibold focus:outline-none focus:ring-2 transition-all"
              style={{
              backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              borderColor: theme === 'dark' ? `rgb(${getAccentColor()})` : `rgb(${getAccentColor()})`,
              color: theme === 'dark' ? `rgb(${getAccentColor()})` : `rgb(${getAccentColor()})`,
              }}
            >
              Back to Dashboard
            </motion.button>
          </div>
        </div>

        {/* App Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 text-center text-sm"
          style={{ color: theme === 'dark' ? '#6b7280' : '#4b5563' }}
        >
          <p>Task Management App • Super Branch</p>
          <p className="mt-1">Built with React, TypeScript, and Supabase</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Profile;
